import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Rocket, ExternalLink, Sparkles, Loader2, Send, TrendingUp, Users, MoreVertical,
  MessageCircle, Calendar, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import VcPipelineKpi, { VcPipelineKpiHandle } from "@/components/sales/VcPipelineKpi";
import { copyToClipboard } from "@/lib/clipboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

interface Fund {
  id: string;
  name: string;
  location: string;
  ticket_min_eur: number;
  ticket_max_eur: number;
  stage: string[];
  sectors: string[];
  volume: string;
  tagline: string;
  website: string;
  contact: string;
  min_traction: string;
  reply_time_days: number;
  notable_portfolio: string[];
  match_score?: number;
}

type FundStatus = "sent" | "replied" | "call" | "signed" | "rejected";

interface FundPipelineRow {
  fund_id: string;
  status: FundStatus;
  amount_eur?: number;
  notes?: string;
}

const STATUS_META: Record<FundStatus, { label: string; className: string; icon: React.ComponentType<{ className?: string }> }> = {
  sent:     { label: "Envoyé",   className: "text-sky-400 border-sky-500/30 bg-sky-500/10",       icon: Send },
  replied:  { label: "Répondu",  className: "text-amber-400 border-amber-500/30 bg-amber-500/10", icon: MessageCircle },
  call:     { label: "Call booké", className: "text-violet-400 border-violet-500/30 bg-violet-500/10", icon: Calendar },
  signed:   { label: "Signé",    className: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10", icon: CheckCircle2 },
  rejected: { label: "Rejeté",   className: "text-rose-400 border-rose-500/30 bg-rose-500/10",    icon: XCircle },
};

const fmtEur = (n: number) => new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 }).format(n) + "€";

export default function PreSeedFundsSection() {
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);
  const [emailCfg, setEmailCfg] = useState<{ configured: boolean } | null>(null);
  const [selected, setSelected] = useState<Fund | null>(null);
  const [note, setNote] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState<{ action_id: string; subject: string; body: string; to: string; needs_recipient?: boolean; suggested_contact_channel?: string | null } | null>(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [statusByFund, setStatusByFund] = useState<Record<string, FundPipelineRow>>({});
  const [signDialog, setSignDialog] = useState<{ fund: Fund } | null>(null);
  const [signAmount, setSignAmount] = useState("");
  const kpiRef = useRef<VcPipelineKpiHandle>(null);

  useEffect(() => {
    (async () => {
      try {
        const [fRes, eRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/preseed/match`, { credentials: "include" }),
          fetch(`${BACKEND_URL}/api/emails/status`, { credentials: "include" }),
        ]);
        const fData = await fRes.json();
        const eData = await eRes.json();
        setFunds(fData.matches || []);
        setEmailCfg(eData);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const refreshAll = async () => {
    await kpiRef.current?.refresh();
  };

  const setFundStatus = async (fund: Fund, status: FundStatus, amount_eur?: number) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/preseed/pipeline/${fund.id}/status`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, ...(amount_eur !== undefined ? { amount_eur } : {}) }),
      });
      if (!r.ok) throw new Error();
      setStatusByFund(prev => ({ ...prev, [fund.id]: { fund_id: fund.id, status, ...(amount_eur !== undefined ? { amount_eur } : {}) } }));
      await refreshAll();
      toast.success(`${fund.name} → ${STATUS_META[status].label}`);
    } catch {
      toast.error("Échec mise à jour du statut");
    }
  };

  const openSignDialog = (fund: Fund) => {
    setSignAmount(String(fund.ticket_min_eur));
    setSignDialog({ fund });
  };

  const confirmSign = async () => {
    if (!signDialog) return;
    const amt = parseFloat(signAmount.replace(",", ".")) || 0;
    await setFundStatus(signDialog.fund, "signed", amt);
    setSignDialog(null);
  };

  const openDraftDialog = (f: Fund) => {
    setSelected(f);
    setDraft(null);
    setNote("");
    setRecipientEmail("");
  };

  const generateDraft = async () => {
    if (!selected) return;
    setDrafting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/preseed/intro-draft`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fund_id: selected.id,
          note,
          recipient_email: recipientEmail.trim() || undefined,
        }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setDraft({
        action_id: d.action_id,
        subject: d.draft.subject,
        body: d.draft.body,
        to: d.draft.to,
        needs_recipient: d.needs_recipient,
        suggested_contact_channel: d.suggested_contact_channel,
      });
      toast.success("Intro préparée par Grafton");
    } catch {
      toast.error("Erreur de génération");
    }
    setDrafting(false);
  };

  const updateRecipient = async () => {
    if (!draft || !recipientEmail.trim()) return;
    try {
      await fetch(`${BACKEND_URL}/api/orchestrator/actions/${draft.action_id}/set-recipient`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: recipientEmail.trim() }),
      });
      setDraft(d => d ? { ...d, to: recipientEmail.trim(), needs_recipient: false } : null);
    } catch { /* tolerated */ }
  };

  const sendReal = async () => {
    if (!draft || !selected) return;
    if (!emailCfg?.configured) {
      toast.error("Connecte Gmail dans l'onglet Messagerie pour envoyer pour de vrai");
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/emails/send/${draft.action_id}`, {
        method: "POST",
        credentials: "include",
      });
      if (r.ok) {
        toast.success("Email envoyé au fonds");
        setStatusByFund(prev => ({ ...prev, [selected.id]: { fund_id: selected.id, status: "sent" } }));
        await refreshAll();
        setSelected(null);
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || "Envoi échoué");
      }
    } catch {
      toast.error("Erreur réseau");
    }
    setSending(false);
  };

  return (
    <div className="space-y-3">
      <VcPipelineKpi ref={kpiRef} onLoaded={(byFund) => setStatusByFund(byFund)} />

      <Card className="bg-card border-border" data-testid="preseed-funds-section">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Rocket className="h-4 w-4 text-primary" />
                Fonds pré-seed français — matchés sur ton profil
              </CardTitle>
              <CardDescription>
                {funds.length} fonds identifiés (Kima, Founders Future, Partech, Alven, Daphni…) — tickets de 50k€ à 3M€ · Investissent avec {"<"}5 clients
              </CardDescription>
            </div>
            {emailCfg && (
              <Badge variant="outline" className={`text-[10px] ${emailCfg.configured ? "text-emerald-400 border-emerald-500/30" : "text-amber-400 border-amber-500/30"}`} data-testid="email-config-badge">
                {emailCfg.configured ? "Envoi réel actif" : "Envoi non configuré"}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="preseed-funds-grid">
              {funds.slice(0, 12).map((f) => {
                const row = statusByFund[f.id];
                const StatusIcon = row ? STATUS_META[row.status].icon : null;
                return (
                  <div key={f.id} className="group border border-border rounded-lg p-3 hover:border-primary/40 hover:bg-primary/5 transition-colors flex flex-col" data-testid={`fund-${f.id}`}>
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-semibold">{f.name}</span>
                          {typeof f.match_score === "number" && (
                            <Badge variant="outline" className={`text-[9px] ${f.match_score >= 80 ? "text-emerald-400 border-emerald-500/30" : "text-muted-foreground"}`}>
                              {f.match_score}% match
                            </Badge>
                          )}
                          {row && StatusIcon && (
                            <Badge variant="outline" className={`text-[9px] ${STATUS_META[row.status].className}`} data-testid={`fund-status-${f.id}`}>
                              <StatusIcon className="h-2.5 w-2.5 mr-0.5" />
                              {STATUS_META[row.status].label}
                              {row.status === "signed" && row.amount_eur ? ` · ${fmtEur(row.amount_eur)}` : ""}
                            </Badge>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground">{f.location}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <a href={f.website} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                        </a>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-0.5 rounded hover:bg-secondary" data-testid={`fund-menu-${f.id}`}>
                              <MoreVertical className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel className="text-[10px] text-muted-foreground">Changer le statut</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => setFundStatus(f, "sent")} data-testid={`mark-sent-${f.id}`}>
                              <Send className="h-3.5 w-3.5 mr-2 text-sky-400" /> Envoyé
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setFundStatus(f, "replied")} data-testid={`mark-replied-${f.id}`}>
                              <MessageCircle className="h-3.5 w-3.5 mr-2 text-amber-400" /> Répondu
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setFundStatus(f, "call")} data-testid={`mark-call-${f.id}`}>
                              <Calendar className="h-3.5 w-3.5 mr-2 text-violet-400" /> Call booké
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openSignDialog(f)} data-testid={`mark-signed-${f.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-emerald-400" /> Signé (avec montant)
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setFundStatus(f, "rejected")} data-testid={`mark-rejected-${f.id}`}>
                              <XCircle className="h-3.5 w-3.5 mr-2 text-rose-400" /> Rejeté
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground my-2 line-clamp-2">{f.tagline}</p>
                    <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                      <Badge variant="outline" className="text-[9px] text-primary">{fmtEur(f.ticket_min_eur)} — {fmtEur(f.ticket_max_eur)}</Badge>
                      {f.stage.slice(0, 2).map((s) => <Badge key={s} variant="outline" className="text-[9px]">{s}</Badge>)}
                    </div>
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1 mb-1">
                      <TrendingUp className="h-3 w-3" /> {f.volume}
                    </p>
                    <p className="text-[10px] text-muted-foreground flex items-start gap-1 mb-3">
                      <Users className="h-3 w-3 mt-0.5 shrink-0" />
                      <span className="line-clamp-2">{f.notable_portfolio.slice(0, 3).join(", ")}</span>
                    </p>
                    <Button size="sm" variant="outline" className="gap-1 mt-auto" onClick={() => openDraftDialog(f)} data-testid={`btn-intro-${f.id}`}>
                      <Sparkles className="h-3 w-3" /> Préparer l'intro
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>

        {/* Draft dialog */}
        <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
          <DialogContent className="max-w-2xl" data-testid="intro-dialog">
            <DialogHeader>
              <DialogTitle>
                Préparer l'intro vers <span className="text-primary">{selected?.name}</span>
              </DialogTitle>
            </DialogHeader>
            {selected && (
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/30 border border-border/40 text-xs space-y-1">
                  <p><b>Ticket</b> : {fmtEur(selected.ticket_min_eur)} — {fmtEur(selected.ticket_max_eur)}</p>
                  <p><b>Stade</b> : {selected.stage.join(", ")}</p>
                  <p><b>Traction attendue</b> : {selected.min_traction}</p>
                  <p><b>Contact</b> : {selected.contact}</p>
                </div>
                {!draft ? (
                  <>
                    <Textarea
                      placeholder="Note optionnelle à ajouter à l'intro (contexte, connexion mutuelle, traction récente…)"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                      data-testid="intro-note"
                    />
                    <Button onClick={generateDraft} disabled={drafting} className="gap-1.5 w-full" data-testid="generate-intro-btn">
                      {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      Générer l'intro avec Grafton
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="p-3 rounded-lg bg-background border border-border space-y-2">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground shrink-0">À :</span>
                        {draft.needs_recipient ? (
                          <div className="flex gap-1 flex-1">
                            <Input
                              type="email"
                              placeholder={draft.suggested_contact_channel ? `Trouvé : ${draft.suggested_contact_channel}` : "email@fonds.com"}
                              value={recipientEmail}
                              onChange={(e) => setRecipientEmail(e.target.value)}
                              className="h-7 text-xs flex-1"
                              data-testid="recipient-email-input"
                            />
                            <Button size="sm" variant="outline" className="h-7" onClick={updateRecipient} disabled={!recipientEmail.trim()}>Valider</Button>
                          </div>
                        ) : (
                          <b className="text-foreground">{draft.to}</b>
                        )}
                      </div>
                      {draft.needs_recipient && draft.suggested_contact_channel && (
                        <p className="text-[10px] text-amber-400">
                          ⚠️ {selected?.name} préfère le canal : <b>{draft.suggested_contact_channel}</b>. Entre l'email que tu souhaites cibler.
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">Sujet : <b className="text-foreground">{draft.subject}</b></p>
                      <pre className="whitespace-pre-wrap text-xs font-sans leading-relaxed bg-muted/30 p-3 rounded-md border border-border/40">{draft.body}</pre>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="gap-1.5 flex-1" onClick={async () => { const ok = await copyToClipboard(draft.body); ok ? toast.success("Copié") : toast.error("Copie bloquée — sélectionne et copie manuellement"); }} data-testid="copy-intro-btn">
                        Copier
                      </Button>
                      <Button className="gap-1.5 flex-1" onClick={sendReal} disabled={sending || !emailCfg?.configured || !draft.to} data-testid="send-real-btn">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {!emailCfg?.configured ? "Gmail non connecté" : !draft.to ? "Saisis un email" : "Envoyer pour de vrai"}
                      </Button>
                    </div>
                    {!emailCfg?.configured && (
                      <p className="text-[10px] text-amber-400 text-center">
                        Connecte ton Gmail dans l'onglet Messagerie pour activer l'envoi réel.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Sign amount dialog */}
        <Dialog open={!!signDialog} onOpenChange={(v) => { if (!v) setSignDialog(null); }}>
          <DialogContent className="max-w-md" data-testid="sign-amount-dialog">
            <DialogHeader>
              <DialogTitle>
                <span className="text-emerald-400">✓</span> Signé avec <span className="text-primary">{signDialog?.fund.name}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <label className="text-xs text-muted-foreground">Montant de la levée (€)</label>
              <Input
                type="number"
                value={signAmount}
                onChange={(e) => setSignAmount(e.target.value)}
                placeholder="150000"
                data-testid="sign-amount-input"
              />
              <p className="text-[10px] text-muted-foreground">
                Range du fonds : {signDialog ? `${fmtEur(signDialog.fund.ticket_min_eur)} — ${fmtEur(signDialog.fund.ticket_max_eur)}` : ""}
              </p>
              <Button className="w-full gap-1.5" onClick={confirmSign} data-testid="confirm-sign-btn">
                <CheckCircle2 className="h-4 w-4" /> Confirmer la signature
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
}
